import { useEffect, useMemo, useState } from "react";
import { AlertCircle, CalendarCheck, CheckCircle2, Clock, Loader2, Search } from "lucide-react";
import { deviceStorage } from "@/lib/deviceStorage";
import { diaryReminders } from "@/lib/grantReminders";
import { getPortalToolPath } from "@/lib/portalToolRoutes";
import GrantRemindersPanel from "@/components/GrantRemindersPanel";
import { listLocalMatters } from "@/lib/aidPlannerStore";
import { getClaimAvailabilityTable } from "@/lib/aidPlannerCriminalFees";
import { diarySuggestions, fillDiaryFromAidPlanner } from "@/lib/diaryAidMatch";
import { isLacwLocalTestMode } from "@/lib/lacwLocalTestStore";
import { DiaryCodeNote, DiaryCodesLegend } from "@/components/DiaryCodes";
import {
  AID_FILTERS,
  AID_STATUS_STYLE,
  AID_WEEK_FORM_TYPE,
  HELD_LISTS_FORM_TYPE,
  HELD_LIST_ORDER,
  matchesAidFilter,
} from "@/lib/billingAidStatus";

// LACW's Aid Status Diary: each week's legal aid status for every listed appearance, as
// Complete Law Support confirmed it on the billing sheet, and the matters held over
// (Aid Pending, To be claimed, Awaiting fee slip). View only: the administrator uploads the
// week once, the Aid Planner fills it, and it is confirmed there (src/lib/billingAidStatus.js).
// LACW read it here; the API lets only an administrator write it.
//
// The preview's test mode has no server, so there the week is the demo's made-up one kept on
// this computer (src/lib/remindersDemo.js), filled from this computer's Aid Planner matters
// the same way the billing sheet fills a real week.

const DEMO_WEEK_KEY = "cls_lacw_aid_diary_active_v1";
const GRANT_STYLES = {
  L: "border-violet-200 bg-violet-50 text-violet-800",
  VU: "border-amber-200 bg-amber-50 text-amber-800",
  VC: "border-emerald-200 bg-emerald-50 text-emerald-800",
  PENDING: "border-orange-200 bg-orange-50 text-orange-800",
};

const dateValue = (value = "") => {
  const match = String(value).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  return match ? new Date(+match[3], +match[2] - 1, +match[1]).getTime() : 0;
};
const latestDate = (rows = []) => Math.max(0, ...rows.map((row) => dateValue(row.date)));
const formatWhen = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })
    : "";
};

async function fetchPublished(formType) {
  const params = new URLSearchParams({ brand: "lacw", form_type: formType });
  const response = await fetch(`/api/vals-drafts?${params}`, { credentials: "include", cache: "no-store" });
  if (!response.ok) throw new Error(`The aid status could not be loaded (${response.status}).`);
  const rows = await response.json();
  return Array.isArray(rows) ? rows : [];
}

function demoWeek() {
  let week = null;
  try { week = JSON.parse(deviceStorage.getItem(DEMO_WEEK_KEY) || "null"); } catch { week = null; }
  const entries = Array.isArray(week?.entries) ? week.entries : [];
  if (!entries.length) return [];
  const suggestions = diarySuggestions(entries, listLocalMatters("lacw"), { availabilityFor: getClaimAvailabilityTable });
  const rows = fillDiaryFromAidPlanner(entries, suggestions);
  return [{ id: "demo-week", weekLabel: week.sourceName || "Demo week (made-up data)", publishedAt: week.savedAt, rows, notesByRow: Object.fromEntries(entries.map((entry) => [entry.id, entry.notes || ""])) }];
}

export default function LACWAidDiary() {
  const testMode = isLacwLocalTestMode();
  const [weeks, setWeeks] = useState([]);
  const [heldItems, setHeldItems] = useState([]);
  const [heldUpdatedAt, setHeldUpdatedAt] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [weekId, setWeekId] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (testMode) {
        setWeeks(demoWeek());
        setLoading(false);
        return;
      }
      try {
        const [weekRows, heldRows] = await Promise.all([fetchPublished(AID_WEEK_FORM_TYPE), fetchPublished(HELD_LISTS_FORM_TYPE)]);
        if (!active) return;
        setWeeks(weekRows
          .map((row) => ({ id: row.id, weekLabel: row.form_data?.weekLabel || row.draft_name, publishedAt: row.form_data?.publishedAt || row.updated_at, rows: Array.isArray(row.form_data?.rows) ? row.form_data.rows : [] }))
          .filter((week) => week.rows.length)
          .sort((a, b) => latestDate(b.rows) - latestDate(a.rows)));
        setHeldItems(Array.isArray(heldRows[0]?.form_data?.items) ? heldRows[0].form_data.items : []);
        setHeldUpdatedAt(heldRows[0]?.form_data?.publishedAt || heldRows[0]?.updated_at || "");
        setLoadError("");
      } catch (error) {
        if (active) setLoadError(error.message);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [testMode]);

  const week = weeks.find((item) => item.id === weekId) || weeks[0] || null;
  const entries = useMemo(
    () => (week?.rows || []).slice().sort((a, b) => dateValue(a.date) - dateValue(b.date)),
    [week],
  );
  const visible = useMemo(() => entries.filter((entry) => {
    if (!matchesAidFilter(entry, filter)) return false;
    const haystack = [entry.clientName, entry.court, entry.lawyer, entry.appearanceType, entry.grantType, entry.aidNumber].join(" ").toLowerCase();
    return !search.trim() || haystack.includes(search.trim().toLowerCase());
  }), [entries, filter, search]);
  const stats = useMemo(() => ({
    total: entries.length,
    confirmed: entries.filter((entry) => entry.confirmed).length,
    funded: entries.filter((entry) => entry.aidStatus === "Aid confirmed" || entry.aidStatus === "LACW funded").length,
    pending: entries.filter((entry) => entry.aidStatus === "Aid pending").length,
  }), [entries]);
  const reminders = useMemo(() => diaryReminders(entries, { source: "lacw" }), [entries]);
  const held = useMemo(() => HELD_LIST_ORDER
    .map((list) => ({ list, items: heldItems.filter((item) => item.list === list).sort((a, b) => dateValue(a.date) - dateValue(b.date)) }))
    .filter((group) => group.items.length), [heldItems]);

  return <main className="mx-auto max-w-[1500px] px-4 py-7 sm:px-6 print:p-0">
    <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-violet-700"><CalendarCheck className="h-4 w-4" /> LACW workflow</div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Aid Status Diary</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">The legal aid status of every listed appearance, week by week, as Complete Law Support has confirmed it. To change something, contact Complete Law Support.</p>
      </div>
      {weeks.length > 0 && (
        <label className="flex flex-col gap-1 text-xs font-bold uppercase tracking-wide text-slate-500 print:hidden">
          Week
          <select value={week?.id || ""} onChange={(event) => setWeekId(event.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold normal-case tracking-normal text-slate-800">
            {weeks.map((item) => <option key={item.id} value={item.id}>{item.weekLabel}</option>)}
          </select>
          {week?.publishedAt && <span className="font-medium normal-case tracking-normal text-slate-500">Updated {formatWhen(week.publishedAt)}</span>}
        </label>
      )}
    </div>

    {loadError && <div role="alert" className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800"><AlertCircle className="mr-2 inline h-4 w-4" />{loadError} Please refresh the page.</div>}

    {loading ? (
      <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>
    ) : !week ? (
      <div className="rounded-2xl border-2 border-dashed border-violet-200 bg-violet-50/50 px-6 py-16 text-center">
        <CalendarCheck className="mx-auto h-10 w-10 text-violet-500" />
        <h2 className="mt-4 text-lg font-bold text-slate-900">No weeks yet</h2>
        <p className="mt-1 text-sm text-slate-500">{testMode ? "Load the reminders demo from the test bar to see a made-up week." : "Each week appears here once Complete Law Support has checked its aid status."}</p>
      </div>
    ) : (
      <>
        <GrantRemindersPanel reminders={reminders} flowchartHref={getPortalToolPath("lacw", "aid-extension-flowchart")} guideHref={getPortalToolPath("lacw", "reminders-guide")} className="mb-6" />
        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[["Listed appearances", stats.total], ["Status confirmed", stats.confirmed], ["Aid confirmed or LACW funded", stats.funded], ["Aid pending", stats.pending]].map(([label, value]) => (
            <div key={label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"><p className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</p><p className="mt-1 text-2xl font-bold text-slate-900">{value}</p></div>
          ))}
        </div>
        <div className="mb-3 flex flex-wrap gap-2 text-[11px] font-semibold text-slate-600">
          <span className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1"><strong>L</strong> — LACW</span>
          <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1"><strong>VU</strong> — Unconfirmed</span>
          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1"><strong>VC</strong> — Confirmed</span>
        </div>
        <div className="mb-4 flex flex-col gap-2 sm:flex-row print:hidden">
          <label className="relative flex-1"><Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search client, court, lawyer, grant code or aid number" className="w-full rounded-lg border border-slate-300 py-2.5 pl-10 pr-3 text-sm" /></label>
          <select aria-label="Aid status" value={filter} onChange={(event) => setFilter(event.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
            {AID_FILTERS.map((option) => <option key={option} value={option}>{option === "All" ? "All statuses" : option}</option>)}
          </select>
        </div>
        <div className="space-y-3">
          {visible.length === 0 && <p className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-500">No appearances match.</p>}
          {visible.map((entry) => (
            <article key={entry.id} className={`rounded-xl border bg-white p-4 shadow-sm ${entry.confirmed ? "border-emerald-200" : "border-amber-200"}`}>
              <div className="grid gap-3 lg:grid-cols-[minmax(200px,1.3fr)_minmax(150px,.9fr)_minmax(200px,1fr)] lg:items-start">
                <div>
                  <p className="flex flex-wrap items-center gap-2 text-sm font-bold text-slate-900">
                    {entry.clientName || "Client not named"}
                    {entry.grantType && <span title="Source diary funding code" className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${GRANT_STYLES[entry.grantType] || "border-slate-200 bg-slate-50 text-slate-700"}`}>{entry.grantType}</span>}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">{[entry.date, entry.court, entry.lawyer && `Lawyer ${entry.lawyer}`].filter(Boolean).join(" · ")}</p>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Appearance</p>
                  <p className="text-sm font-semibold text-slate-800">{entry.appearanceType || "—"}</p>
                  <DiaryCodeNote text={entry.appearanceType} clientName={entry.clientName} />
                </div>
                <div>
                  <span className={`inline-flex rounded-lg border px-2 py-1 text-xs font-bold ${AID_STATUS_STYLE[entry.aidStatus] || AID_STATUS_STYLE["Not checked"]}`}>{entry.aidStatus || "Not checked"}</span>
                  {entry.aidNumber && <span className="ml-2 text-xs font-semibold text-slate-700">{entry.aidNumber}</span>}
                  <p className={`mt-1 flex items-center gap-1 text-[11px] font-semibold ${entry.confirmed ? "text-emerald-700" : "text-amber-800"}`}>
                    {entry.confirmed ? <><CheckCircle2 className="h-3.5 w-3.5" /> Confirmed{entry.confirmedAt ? ` ${formatWhen(entry.confirmedAt)}` : ""}</> : <><Clock className="h-3.5 w-3.5" /> Not yet confirmed</>}
                  </p>
                  {entry.reason && <p className="mt-1 text-[11px] text-slate-600">{entry.reason}</p>}
                  {entry.aidPlannerFill?.reason && <p className="mt-1 text-[11px] text-slate-600">{entry.aidPlannerFill.reason}</p>}
                  {week.notesByRow?.[entry.id] && <p className="mt-1 text-[11px] italic text-slate-500">{week.notesByRow[entry.id]}</p>}
                </div>
              </div>
            </article>
          ))}
        </div>
      </>
    )}

    {held.length > 0 && (
      <section className="mt-8">
        <h2 className="text-lg font-bold text-slate-900">Held over to later weeks</h2>
        <p className="mt-1 text-xs text-slate-500">Matters carried from week to week until aid is approved, they are ready to claim, or counsel's fee slip arrives.{heldUpdatedAt ? ` Updated ${formatWhen(heldUpdatedAt)}.` : ""}</p>
        <div className="mt-3 grid gap-4 lg:grid-cols-3">
          {held.map((group) => (
            <div key={group.list} className="rounded-xl border border-slate-200 bg-white p-4">
              <h3 className="text-sm font-bold text-slate-900">{group.list} <span className="font-semibold text-slate-500">({group.items.length})</span></h3>
              <ul className="mt-2 space-y-2">
                {group.items.map((item, index) => (
                  <li key={`${item.clientName}-${item.date}-${index}`} className="text-xs text-slate-700">
                    <span className="font-semibold">{item.clientName || "Client not named"}</span>
                    <span className="text-slate-500"> · {[item.date, item.appearanceType, item.court].filter(Boolean).join(" · ")}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    )}

    <DiaryCodesLegend sheetHref={getPortalToolPath("lacw", "diary-codes")} className="mt-8" />
  </main>;
}
